import React from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@pagespeedify/ui';
import {
  TrashIcon,
  CheckCircleIcon,
  AlertCircleIcon,
  ClockIcon,
  MinusIcon,
  PlayIcon,
  EyeIcon,
  EyeOffIcon
} from 'lucide-react';
import { useTaskQueue, QueuedTask } from './TaskQueueContext';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../../lib/apiClient';
import { toast } from 'sonner';
import { Task } from '../../types';
import { useQueryClient } from '@tanstack/react-query';

const TaskQueuePanel: React.FC = () => {  const {
    queuedTasks,
    removeTaskFromQueue,
    clearQueue,
    clearCompletedTasks,
    updateTaskStatus,
    isQueueVisible,
    setQueueVisible,
    isProcessing,
    setIsProcessing
  } = useTaskQueue();
  
  const apiClient = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  if (!isQueueVisible && queuedTasks.length === 0) {
    return null;
  }

  const getStatusIcon = (status: QueuedTask['status']) => {
    switch (status) {
      case 'queued':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 'creating':
        return <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />;
      case 'created':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: QueuedTask['status']) => {
    switch (status) {
      case 'queued': return 'Queued';
      case 'creating': return 'Creating...';
      case 'created': return 'Created';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };
  const handleProcessQueue = async () => {
    if (isProcessing) return;
    
    const queuedItems = queuedTasks.filter(task => task.status === 'queued');
    if (queuedItems.length === 0) {
      toast.info('No tasks in queue to process');
      return;
    }

    setIsProcessing(true);
    // Mark all as 'creating' for UI
    queuedItems.forEach(task => updateTaskStatus(task.id, 'creating'));

    // Prepare payload: remove queue-specific fields
    const batchPayload = queuedItems.map(({ id, queued_at, status, error, ...taskPayload }) => taskPayload);
    let successCount = 0;
    let errorCount = 0;
    let createdTasks: Task[] = [];
    try {
      // Batch POST
      const response = await apiClient.post<any>('/tasks', batchPayload);
      // If backend returns array of created tasks, mark all as created
      if (Array.isArray(response)) {
        successCount = response.length;
        createdTasks = response;
        queuedItems.forEach(task => updateTaskStatus(task.id, 'created'));
      } else if (response && response.success && Array.isArray(response.results)) {
        // If backend returns per-task results
        response.results.forEach((result: any, idx: number) => {
          if (result.success) {
            updateTaskStatus(queuedItems[idx].id, 'created');
            successCount++;
            createdTasks.push(result.task);
          } else {
            updateTaskStatus(queuedItems[idx].id, 'failed', result.error || 'Failed');
            errorCount++;
          }
        });
      } else {
        // Fallback: assume all succeeded
        queuedItems.forEach(task => updateTaskStatus(task.id, 'created'));
        successCount = queuedItems.length;
      }
    } catch (err: any) {
      // If batch fails, mark all as failed
      queuedItems.forEach(task => updateTaskStatus(task.id, 'failed', err?.message || 'Batch failed'));
      errorCount = queuedItems.length;
    }

    setIsProcessing(false);
    // Show results
    if (successCount > 0) {
      toast.success(
        `${successCount} task${successCount !== 1 ? 's' : ''} created successfully` +
        (errorCount > 0 ? ` (${errorCount} failed)` : '')
      );
      queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
      // Navigate to the first created task's AI solutions page
      if (createdTasks.length > 0) {
        setTimeout(() => {
            navigate(`/tasks/${createdTasks[0].id}/solution`);
        }, 1500);
      }
    } else {
      toast.error('Unable to create tasks. Please try again.');
    }
  };

  const queuedCount = queuedTasks.filter(t => t.status === 'queued').length;
  const createdCount = queuedTasks.filter(t => t.status === 'created').length;
  const failedCount = queuedTasks.filter(t => t.status === 'failed').length;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-96 max-h-[70vh] overflow-hidden" data-tour="task-queue">
      <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <ClockIcon className="h-5 w-5" />
              Task Queue ({queuedTasks.length})
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQueueVisible(!isQueueVisible)}
                className="h-8 w-8 p-0"
              >
                {isQueueVisible ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearQueue}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                disabled={isProcessing}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {queuedTasks.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {queuedCount > 0 && <span className="text-blue-600">{queuedCount} queued</span>}
              {createdCount > 0 && <span className="text-green-600 ml-2">{createdCount} created</span>}
              {failedCount > 0 && <span className="text-red-600 ml-2">{failedCount} failed</span>}
            </div>
          )}
        </CardHeader>
        
        {isQueueVisible && (
          <CardContent className="pt-0">
            {queuedTasks.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No tasks in queue. Add tasks from performance reports.
              </p>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto space-y-2 mb-4 no-scrollbar">
                  {queuedTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getStatusIcon(task.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{task.title}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <span>{getStatusText(task.status)}</span>
                          <span>•</span>
                          <span className="capitalize">{task.priority}</span>
                        </div>
                        {task.error && (
                          <p className="text-xs text-red-500 mt-1">{task.error}</p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTaskFromQueue(task.id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        disabled={isProcessing && task.status === 'creating'}
                      >
                        <MinusIcon className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                  {queuedCount > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleProcessQueue}
                      disabled={isProcessing}
                      className="flex-1"
                      size="sm"
                      data-tour="create-tasks-from-queue"
                    >
                      {isProcessing ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Creating Tasks...
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4 mr-2" />
                          Create {queuedCount} Task{queuedCount !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                    {(createdCount > 0 || failedCount > 0) && (
                      <Button
                        onClick={clearCompletedTasks}
                        disabled={isProcessing}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default TaskQueuePanel;
